import { useTranslation } from 'react-i18next';
import { Lock, ShieldCheck } from 'lucide-react';
import { useApi } from '@/Hooks/useApi';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { SummaryRow } from '@/components/ui/detail';
import { LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { FLEET_ROUTES, type DeviceDiagnostics } from '../fleetApi';
import { DeviceHealthChip, JobStatusChip, PrinterStatusChip, VersionChip } from '../fleetVisuals';
import { formatDateTime } from '../fleetHelpers';

/**
 * Everything known about one device, in the order somebody troubleshooting asks
 * for it: what it is, whether it is alive, what it runs, and — if it is a print
 * bridge — what it has been failing to print.
 *
 * The bridge half is the reason this exists rather than a row expander. "The
 * tablet is fine, the printer behind it is out of paper" is the answer that
 * stops a support call becoming a site visit, and it cannot be given without
 * putting the device's health and its printers' statuses on the same surface.
 *
 * There are no document payloads here, deliberately: a diagnostics page is read
 * over shoulders.
 */
export default function DeviceDiagnosticsDialog({
  deviceId,
  onClose,
}: {
  deviceId: string | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const query = useApi<DeviceDiagnostics>(
    deviceId ? FLEET_ROUTES.deviceDiagnostics(deviceId) : null,
  );
  const data = query.data;

  return (
    <Dialog
      open={deviceId !== null}
      onClose={onClose}
      title={t('fleet.diagnostics.title')}
      description={t('fleet.diagnostics.subtitle')}
      className="max-w-3xl"
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {query.loading ? (
        <LoadingState />
      ) : query.error ? (
        <div
          role="alert"
          className="rounded-lg border border-red-300 bg-red-50/70 p-4 text-sm dark:border-red-900 dark:bg-red-950/30"
        >
          <p className="font-medium">
            {t('fleet.states.loadFailed', { subject: t('fleet.diagnostics.subject') })}
          </p>
          <p className="mt-1 text-ink-muted">{query.error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={query.reload}>
            {t('common.tryAgain')}
          </Button>
        </div>
      ) : data ? (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <SummaryRow label={t('fleet.devices.name')}>{data.device.name}</SummaryRow>
              <SummaryRow label={t('fleet.common.type')}>
                {t(`fleet.deviceType.${data.device.type}`)}
              </SummaryRow>
              <SummaryRow label={t('fleet.common.branch')}>
                {data.device.branch?.name ?? t('fleet.common.noBranch')}
              </SummaryRow>
              <SummaryRow label={t('fleet.common.health')}>
                <DeviceHealthChip health={data.health} />
              </SummaryRow>
              <SummaryRow label={t('fleet.devices.lastSeen')}>
                {data.device.lastSeenAt
                  ? formatDateTime(data.device.lastSeenAt, i18n.language)
                  : t('fleet.common.never')}
              </SummaryRow>
            </div>
            <div className="space-y-1.5">
              <SummaryRow label={t('fleet.common.version')}>
                <VersionChip compliance={data.version.compliance} />
              </SummaryRow>
              <SummaryRow label={t('fleet.diagnostics.reportedVersion')}>
                {data.version.reportedVersion ?? t('fleet.diagnostics.neverReported')}
              </SummaryRow>
              <SummaryRow label={t('fleet.diagnostics.minSupported')}>
                {data.version.minSupportedVersion ?? t('fleet.versions.noPolicy')}
              </SummaryRow>
              <SummaryRow label={t('fleet.diagnostics.platform')}>
                {[data.device.platform, data.device.osVersion].filter(Boolean).join(' ') ||
                  t('fleet.common.dash')}
              </SummaryRow>
              <SummaryRow label={t('fleet.diagnostics.fingerprint')}>
                <span className="break-all font-mono text-xs">
                  {data.device.fingerprint ?? t('fleet.diagnostics.fingerprintPending')}
                </span>
              </SummaryRow>
            </div>
          </div>

          {/* The fingerprint is shown because support has to match a row against
              a tablet in someone's hand. It is an identifier and never a
              credential — it is client-generated and travels in plaintext — and
              saying so here is what stops it being treated as a secret. */}
          <p className="flex items-start gap-2 text-xs text-ink-muted">
            <Icon as={ShieldCheck} className="mt-0.5 shrink-0" />
            {t('fleet.diagnostics.fingerprintNote')}
          </p>

          {data.device.revokedAt && (
            <div className="rounded-lg border p-3 text-sm">
              <p className="font-medium">{t('fleet.diagnostics.revokedOn')}</p>
              <p className="text-ink-muted">
                {formatDateTime(data.device.revokedAt, i18n.language)}
                {data.device.revokedReason ? ` — ${data.device.revokedReason}` : ''}
              </p>
            </div>
          )}

          {/* A PIN lockout locks the *device*, not the account, because a
              fingerprint is client-generated and travels in plaintext — it is an
              identifier, never a credential. A staff member locked out at one
              till is the visible symptom, and this is where it is explained. */}
          {data.device.pinLockedUntil && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
              <Icon as={Lock} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-medium">{t('fleet.diagnostics.pinLocked')}</p>
                <p className="text-ink-muted">
                  {t('fleet.diagnostics.pinLockedUntil', {
                    time: formatDateTime(data.device.pinLockedUntil, i18n.language),
                    attempts: data.device.pinAttempts,
                  })}
                </p>
              </div>
            </div>
          )}

          {data.bridge && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">{t('fleet.diagnostics.bridgeTitle')}</h4>
              <div className="grid gap-1.5 sm:grid-cols-2">
                <SummaryRow label={t('fleet.common.name')}>{data.bridge.name}</SummaryRow>
                <SummaryRow label={t('fleet.bridges.agentVersion')}>
                  {data.bridge.agentVersion ?? t('fleet.common.dash')}
                </SummaryRow>
                <SummaryRow label={t('fleet.bridges.lastPoll')}>
                  {data.bridge.lastSeenAt
                    ? formatDateTime(data.bridge.lastSeenAt, i18n.language)
                    : t('fleet.common.never')}
                </SummaryRow>
                <SummaryRow label={t('fleet.common.status')}>
                  {data.bridge.isActive
                    ? t('fleet.bridges.enrolled')
                    : t('fleet.bridges.revokedStatus')}
                </SummaryRow>
              </div>

              {data.bridge.printers.length > 0 && (
                <TableWrap>
                  <Table>
                    <THead>
                      <TR>
                        <TH>{t('fleet.common.printer')}</TH>
                        <TH>{t('fleet.common.status')}</TH>
                        <TH>{t('fleet.printers.lastReport')}</TH>
                        <TH>{t('fleet.printers.printerSaid')}</TH>
                      </TR>
                    </THead>
                    <tbody>
                      {data.bridge.printers.map((printer) => (
                        <TR key={printer.id}>
                          <TD>
                            <span className="font-medium">{printer.name}</span>
                            <p className="text-xs text-ink-muted">{printer.code}</p>
                          </TD>
                          <TD>
                            <PrinterStatusChip status={printer.lastStatus ?? 'UNKNOWN'} />
                          </TD>
                          <TD className="text-ink-muted">
                            {printer.lastStatusAt
                              ? formatDateTime(printer.lastStatusAt, i18n.language)
                              : t('fleet.common.never')}
                          </TD>
                          <TD className="text-ink-muted">
                            {printer.lastStatusDetail ?? t('fleet.common.dash')}
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </div>
          )}

          {data.bridge && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">{t('fleet.diagnostics.recentFailures')}</h4>
              {data.recentPrintFailures.length === 0 ? (
                <p className="rounded-lg border border-dashed p-4 text-sm text-ink-muted">
                  {t('fleet.diagnostics.noRecentFailures')}
                </p>
              ) : (
                <TableWrap>
                  <Table>
                    <THead>
                      <TR>
                        <TH>{t('fleet.spool.document')}</TH>
                        <TH>{t('fleet.common.status')}</TH>
                        <TH>{t('fleet.spool.attempts')}</TH>
                        <TH>{t('fleet.spool.whatWentWrong')}</TH>
                        <TH>{t('fleet.jobDetail.failedAt')}</TH>
                      </TR>
                    </THead>
                    <tbody>
                      {data.recentPrintFailures.map((failure) => (
                        <TR key={failure.id}>
                          <TD>{t(`fleet.documentType.${failure.documentType}`)}</TD>
                          <TD>
                            <JobStatusChip status={failure.status} />
                          </TD>
                          <TD className="tabular-nums">{failure.attempts}</TD>
                          <TD className="max-w-[18rem] break-words text-red-700 dark:text-red-400">
                            {failure.lastError ?? t('fleet.common.dash')}
                          </TD>
                          <TD className="text-ink-muted">
                            {failure.failedAt
                              ? formatDateTime(failure.failedAt, i18n.language)
                              : t('fleet.common.dash')}
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </div>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
